import React, { useState } from 'react';
import { Briefcase, Settings, User, Wand2, Images, Stamp, ChevronLeft, ChevronRight, Coins } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { useGenContext } from '../contexts/GenContext';
import { useWallet } from '../hooks/useWallet';
import FloatingAssistant from './FloatingAssistant';
import AvatarCropperModal from './AvatarCropperModal';

const menuItems = [
    { id: 'toolkit', label: '里務工具包', icon: Briefcase },
    { id: 'style-transfer', label: '人像風格生成', icon: Wand2 },
    { id: 'gallery', label: '圖片庫', icon: Images },
    { id: 'stamp-manager', label: '印章管理', icon: Stamp },
    { id: 'settings', label: '系統設定區', icon: Settings },
];

interface SidebarProps {
    /** 手機 drawer 是否開啟（桌面 ≥lg 永遠展開，此 prop 無作用） */
    mobileOpen?: boolean;
    /** 手機點選 nav item 或 logout 後關閉 drawer */
    onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
    const { globalProfile, globalProfileThumbnail, name, title, organizationName, uploadProfileImage, isAuthenticated } = useAppContext();
    const subtitle = [organizationName, title].filter(Boolean).join(' ');
    const { activeTab, setActiveTab } = useGenContext();
    // 摺疊狀態僅作用於桌面（≥lg）；手機固定為展開
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { wallet } = useWallet(isAuthenticated);
    // 待裁切的檔案；非 null 時顯示裁切彈窗
    const [cropFile, setCropFile] = useState<File | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';   // 清空才能重複選同一個檔案
        if (file) setCropFile(file);
    };

    const handleCropped = (croppedFile: File) => {
        setCropFile(null);
        void uploadProfileImage(croppedFile);
    };

    // 手機點 nav 後關 drawer
    const handleNavClick = (id: string) => {
        setActiveTab(id);
        onMobileClose?.();
    };

    // 手機 drawer 滑入動畫：未開啟時 -translate-x-full；lg 以上強制歸位
    const mobileTransform = mobileOpen ? 'translate-x-0' : '-translate-x-full';

    return (
        <aside
            className={`${isCollapsed ? 'lg:w-16' : 'lg:w-64'} w-64 fixed inset-y-0 left-0 z-40 ${mobileTransform} lg:translate-x-0 lg:static transition-[transform,width] duration-300 ease-in-out border-r border-slate-200 dark:border-slate-800 bg-white/95 lg:bg-white/80 dark:bg-slate-900/95 lg:dark:bg-slate-900/50 backdrop-blur-md flex flex-col h-full overflow-hidden`}
        >

            {/* Header */}
            <div className={`flex items-center ${isCollapsed ? 'justify-center px-0 pt-5 pb-4' : 'gap-3 px-5 pt-6 pb-6'} relative`}>
                <div className="w-10 h-10 shrink-0 rounded-xl overflow-hidden shadow-lg shadow-blue-500/20">
                    <img src="/favicon-128x128.png" alt="logo" className="w-full h-full object-cover" />
                </div>
                {!isCollapsed && (
                    <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white whitespace-nowrap">
                        里嗨AI
                    </h1>
                )}
                {/* Toggle button — only visible when expanded; 手機不顯示收合按鈕 */}
                {!isCollapsed && (
                    <button
                        onClick={() => setIsCollapsed(true)}
                        className="ml-auto p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors focus:outline-none hidden lg:block"
                        title="收合側欄"
                    >
                        <ChevronLeft size={16} />
                    </button>
                )}
            </div>

            {/* Expand button when collapsed */}
            {isCollapsed && (
                <div className="flex justify-center pb-3">
                    <button
                        onClick={() => setIsCollapsed(false)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors focus:outline-none"
                        title="展開側欄"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}

            {/* Nav */}
            <div className={`${isCollapsed ? 'px-2' : 'px-4'} space-y-1`}>
                {menuItems.map(item => (
                    <div key={item.id} className="relative group/item">
                        <button
                            onClick={() => handleNavClick(item.id)}
                            className={`w-full flex items-center py-3 rounded-xl transition-all focus:outline-none ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-3'
                                } ${activeTab === item.id
                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
                                }`}
                        >
                            <item.icon size={20} className="shrink-0" />
                            {!isCollapsed && (
                                <span className="font-medium">{item.label}</span>
                            )}
                        </button>
                        {/* Tooltip (collapsed only) */}
                        {isCollapsed && (
                            <span className="pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-lg px-2.5 py-1.5 opacity-0 group-hover/item:opacity-100 transition-opacity duration-150 whitespace-nowrap z-50 shadow-lg">
                                {item.label}
                            </span>
                        )}
                    </div>
                ))}

            </div>

            {/* 底部區塊：Profile + AI 小幫手 */}
            <div className="mt-auto">
                {/* Profile section — 摺疊時隱藏 */}
                {!isCollapsed && (
                    <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                        <div className="relative group">
                            <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700/50">
                                <div className="relative w-32 h-32 rounded-full overflow-hidden border-2 border-blue-500/30 bg-slate-200 dark:bg-slate-700 group-hover:border-blue-500 transition-all cursor-pointer">
                                    {globalProfile ? (
                                        <img src={globalProfileThumbnail || globalProfile} alt="Representative" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                                            <User size={48} />
                                        </div>
                                    )}
                                    <input type="file" accept="image/*" onChange={handleFileChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer" />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{name}</p>
                                    {subtitle && <p className="text-xs text-slate-500 dark:text-slate-500">{subtitle}</p>}
                                </div>
                                {wallet && (() => {
                                    const totalGranted = wallet.pointLogs.reduce((sum, log) => sum + (log.delta > 0 ? log.delta : 0), 0) || wallet.initialPoints;
                                    const remainingRatio = wallet.currentPoints / totalGranted;
                                    const usedPoints = totalGranted - wallet.currentPoints;
                                    const usedPct = Math.max(0, Math.min(100, (usedPoints / totalGranted) * 100));
                                    const colorClass = remainingRatio > 0.3
                                        ? 'bg-blue-500'
                                        : remainingRatio > 0.1
                                            ? 'bg-amber-400'
                                            : 'bg-red-400';
                                    return (
                                        <div className="w-full px-1">
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1 whitespace-nowrap">
                                                    <Coins size={11} />
                                                    已使用點數
                                                </span>
                                                <span className="font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                                    {usedPct.toFixed(0)}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                                                <div
                                                    className={`h-1.5 rounded-full transition-all duration-300 ${colorClass}`}
                                                    style={{ width: `${usedPct}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-end text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 whitespace-nowrap">
                                                {usedPoints} / {totalGranted}
                                            </div>
                                        </div>
                                    );
                                })()}
                                <button className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-bold transition-colors">
                                    點擊圖片更換形象照
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* AI 小幫手 — 登入後才顯示 */}
                {isAuthenticated && (
                    <div className={`border-t border-slate-200 dark:border-slate-800 ${isCollapsed ? 'py-2' : 'py-1'}`}>
                        {isCollapsed && wallet && (
                            <div className="relative group/points flex justify-center py-1">
                                <button className="flex flex-col items-center gap-0.5 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                    <Coins size={16} className={(() => {
                                        const tg = wallet.pointLogs.reduce((s, l) => s + (l.delta > 0 ? l.delta : 0), 0) || wallet.initialPoints;
                                        const r = wallet.currentPoints / tg;
                                        return r > 0.3 ? 'text-blue-500' : r > 0.1 ? 'text-amber-400' : 'text-red-400';
                                    })()} />
                                    <span className="text-[9px] font-bold text-slate-600 dark:text-slate-400 leading-none">
                                        {wallet.currentPoints}
                                    </span>
                                </button>
                                <span className="pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-lg px-2.5 py-1.5 opacity-0 group-hover/points:opacity-100 transition-opacity duration-150 whitespace-nowrap z-50 shadow-lg">
                                    本月剩餘 {wallet.currentPoints} / {(wallet.pointLogs.reduce((s, l) => s + (l.delta > 0 ? l.delta : 0), 0) || wallet.initialPoints)} 點
                                </span>
                            </div>
                        )}
                        <FloatingAssistant isCollapsed={isCollapsed} />
                    </div>
                )}
            </div>

            {cropFile && (
                <AvatarCropperModal
                    file={cropFile}
                    onCancel={() => setCropFile(null)}
                    onConfirm={handleCropped}
                />
            )}
        </aside>
    );
};

export default Sidebar;
